import { BOAT } from "../../../shared/Config";
import {
    coercePlayer,
    EquippedBoat,
    freshPlayer,
    coerceEquipped,
    loadoutOf,
    PlayerStore,
    Purchase,
    sanitiseName,
    STORAGE_KEY,
    StoredPlayerData
} from "./Player";

/**
 * The one implementation of [PlayerStore](Player.ts), on `localStorage`.
 *
 * **The only file in the game that mentions `localStorage`.** That is the whole point of the split:
 * server-owned accounts are coming, and when they do this file is replaced by one that makes
 * requests while nothing else in the game changes. See design/AccountData.md and design/Shop.md.
 *
 * Three rules from design/AccountData.md, all of them load-bearing:
 *
 * - **Every read survives garbage.** Absent, truncated, hand-edited, from a future version — none
 *   of those is an error state, and the player is never told about any of them. A failed parse
 *   falls back to defaults and the game carries on.
 * - **Private-mode and disabled-storage browsers work.** If `localStorage` throws, the same
 *   structure is held in memory for the session and dies at the tab close. The game is fully
 *   playable with zero persistence.
 * - **Written on meaningful change only** — a life ending, a purchase, a name change. Never per
 *   frame, never per tick.
 */
export class LocalStore implements PlayerStore {
    /**
     * The blob as it stands, once it has been read. Also the whole of the store on a browser that
     * refuses storage: `_persist` failing does not stop this being updated, so a session with no
     * disk behind it behaves exactly like one with disk until the tab closes.
     */
    private _data: StoredPlayerData | null = null;

    /**
     * The read in flight, so ten callers at boot produce one parse rather than ten. `load` is
     * called from the toolbar, from the scene and from anything the shop grows, and none of them
     * should have to know whether it is first.
     */
    private _reading: Promise<StoredPlayerData> | null = null;

    public load(): Promise<StoredPlayerData> {
        if (this._data) return Promise.resolve(this._data);
        if (!this._reading) this._reading = Promise.resolve().then(() => this._read());

        return this._reading;
    }

    public async setName(name: string): Promise<void> {
        const trimmed = sanitiseName(name);
        // An empty name is the toolbar's guest label rather than a name the player typed, and
        // storing it would overwrite a real one with a placeholder on the next load.
        if (trimmed.length === 0) return;

        await this._update((data) => {
            data.displayName = trimmed;
        });
    }

    public async bank(doubloons: number): Promise<void> {
        const earned = Math.floor(Number(doubloons));
        if (!Number.isFinite(earned) || earned <= 0) return;

        await this._update((data) => {
            data.wallet.doubloons += earned;
            data.lastPlayedAt = Date.now();
            data.records.gamesPlayed += 1;
        });
    }

    public async equip(boat: EquippedBoat): Promise<void> {
        await this._update((data) => {
            // Clamped through the same function the arena runs on a joining client, so a boat that
            // has left the catalogue since it was equipped becomes the free one rather than a
            // number nothing can resolve.
            const loadout = loadoutOf(boat);
            // The colours go back through the same coercion the load path uses, so what is
            // written is what a reload would read — a picker that offered something the tree does
            // not allow cannot leave it in the file.
            data.locker.equipped = coerceEquipped({ ...boat, preset: loadout.preset, cannons: [...loadout.cannons] });
        });
    }

    /**
     * Debit and ownership together, or neither.
     *
     * Refusing is an **ordinary answer**, not an error: a shop that lets a button be pressed while
     * the wallet is short is a shop that has to say no, and a rejected promise would make that a
     * failure the caller has to catch rather than a `false` it can read.
     */
    public async spend(purchase: Purchase): Promise<boolean> {
        const cost = Math.max(0, Math.floor(Number(purchase.cost) || 0));
        const catalogue = purchase.kind === "preset" ? BOAT.presets.length : BOAT.cannons.length;
        const index = Math.floor(Number(purchase.index));
        if (!Number.isFinite(index) || index < 0 || index >= catalogue) return false;

        const data = await this.load();
        const owned = purchase.kind === "preset" ? data.locker.presets : data.locker.cannons;
        // Already owned is not a failure and not a second charge — it is a shop that has been
        // clicked twice, which is the likeliest thing to happen to it.
        if (owned.includes(index)) return true;
        if (data.wallet.doubloons < cost) return false;

        await this._update((current) => {
            const list = purchase.kind === "preset" ? current.locker.presets : current.locker.cannons;
            current.wallet.doubloons -= cost;
            list.push(index);
            list.sort((a, b) => a - b);
        });

        return true;
    }

    /** Reads and coerces once. Anything at all that goes wrong is a fresh player, silently. */
    private _read(): StoredPlayerData {
        let raw: string | null = null;
        try {
            raw = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
        } catch {
            // A browser that refuses storage entirely. Nothing to say about it: the session runs
            // in memory and forgets at the tab close, which is a documented supported state.
        }

        if (raw === null) {
            this._data = freshPlayer();
            return this._data;
        }

        try {
            this._data = coercePlayer(JSON.parse(raw));
        } catch {
            // Truncated, or not JSON at all. A failed parse is not an error state — see the class
            // comment — so it is a fresh player and the next write replaces the wreckage.
            this._data = freshPlayer();
        }

        return this._data;
    }

    /** Applies one change and persists. The single write path, so nothing else can forget to save. */
    private async _update(change: (data: StoredPlayerData) => void): Promise<void> {
        const data = await this.load();
        change(data);
        this._persist(data);
    }

    /**
     * Writes, or does not. A browser with storage disabled throws here on every write and the
     * session carries on against `_data` — which is why the change above is applied before this
     * runs rather than after it succeeds.
     */
    private _persist(data: StoredPlayerData): void {
        try {
            globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch {
            // Quota, private mode, storage switched off. The player is never told: they are
            // playing a game, and this is a convenience rather than a promise.
        }
    }
}

/**
 * The store the game uses. One instance, because it caches the blob and two would be two answers
 * to "how much money do I have".
 */
export const player: PlayerStore = new LocalStore();
